// Partner Brands data hooks. Reads register under partnerBrandKeys; every
// mutation invalidates the prefix so the list + detail refetch (stale-after-
// save convention).
//
// partnerBrandKeys and prequalSiteAdminKeys are two query families over
// OVERLAPPING data. The record-scoped Prequalification tab on a Broker /
// Brokerage page derives its entire shape from the brands table — the
// resolution (Own / Inherited / None), the site card's displayName and
// isActive, and the firm's members-with-own-sites roll-up. The QueryClient
// runs staleTime 5min with refetchOnWindowFocus off, so a mutation that
// refreshes only ONE family leaves the other showing a stale answer with no
// recovery short of a reload: create a site from a broker's Prequalification
// tab, navigate back, and the cached "No prequalification site" empty state
// survives for five minutes.
//
// Hence the rule: any mutation that changes a brand's EXISTENCE, OWNERSHIP, or
// ACTIVE state goes through invalidatePartnerBrandAndSiteReads. Mutations that
// change neither (the logo slots) stay on partnerBrandKeys alone — nothing on
// the site card is derived from a logo.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import {
  partnerBrandsApi,
  type CreatePartnerBrandRequest,
  type PartnerBrand,
  type PartnerBrandDeleteEligibility,
  type PartnerBrandSummary,
  type PartnerLogoVariant,
  type UpdatePartnerBrandRequest,
} from "@/api/partnerBrands";
import { partnerBrandKeys } from "./partnerBrandKeys";
import { prequalSiteAdminKeys } from "./prequalSiteAdminKeys";

/** Cover BOTH query families over the brands table. Call this from every
 *  mutation that changes a brand's existence, ownership, or active state —
 *  see the module header for why one family alone is not enough. Exported so
 *  callers outside this module invalidate by helper rather than hand-listing
 *  two key prefixes and forgetting the second. */
export function invalidatePartnerBrandAndSiteReads(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: partnerBrandKeys.all }),
    qc.invalidateQueries({ queryKey: prequalSiteAdminKeys.all }),
  ]);
}

export function usePartnerBrands() {
  return useQuery<PartnerBrandSummary[]>({
    queryKey: partnerBrandKeys.list(),
    queryFn: () => partnerBrandsApi.list(),
  });
}

export function usePartnerBrand(id: string | undefined) {
  return useQuery<PartnerBrand>({
    queryKey: partnerBrandKeys.detail(id ?? ""),
    queryFn: () => partnerBrandsApi.get(id!),
    enabled: !!id,
  });
}

/** Creates a brand — and, when the payload names an owner, the record-scoped
 *  read for that broker/brokerage flips from "None" to "Own" in the same
 *  breath. Existence + ownership → both families. */
export function useCreatePartnerBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePartnerBrandRequest) => partnerBrandsApi.create(body),
    onSuccess: () => {
      void invalidatePartnerBrandAndSiteReads(qc);
    },
  });
}

/** The edit payload carries ownerBrokerId / ownerBrokerageId, so an update can
 *  re-home a site between records (and it renames the card either way). */
export function useUpdatePartnerBrand(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePartnerBrandRequest) => partnerBrandsApi.update(id, body),
    onSuccess: () => {
      void invalidatePartnerBrandAndSiteReads(qc);
    },
  });
}

/** isActive is rendered on the site card, so publishing/unpublishing has to
 *  reach the record-scoped read too. */
export function useSetPartnerBrandActive(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) => partnerBrandsApi.setActive(id, isActive),
    onSuccess: () => {
      void invalidatePartnerBrandAndSiteReads(qc);
    },
  });
}

/** Whether this site can be deleted, and every blocker if not. Read BEFORE
 *  offering delete so the confirm states the consequence; render the server's
 *  `refusalMessage` verbatim rather than composing a second one. */
export function usePartnerBrandDeleteEligibility(id: string | undefined) {
  return useQuery<PartnerBrandDeleteEligibility>({
    queryKey: partnerBrandKeys.deleteEligibility(id ?? ""),
    queryFn: () => partnerBrandsApi.deleteEligibility(id!),
    enabled: !!id,
  });
}

/** Deletes a site. Existence changes, so every inheriting broker's resolution
 *  and every owner's site card have to be re-read — both families. */
export function useDeletePartnerBrand(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => partnerBrandsApi.remove(id),
    onSuccess: () => {
      void invalidatePartnerBrandAndSiteReads(qc);
    },
  });
}

// ── Logo mutations ────────────────────────────────────────────────────────
// Deliberately partnerBrandKeys ONLY. A logo changes no brand's existence,
// ownership, or active state, and PrequalSiteCard carries no logo field — so
// the record-scoped read cannot go stale from one. If the site card ever
// starts rendering a logo, these move onto the shared helper.

export function useUploadPartnerBrandLogo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => partnerBrandsApi.uploadLogo(id, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partnerBrandKeys.all });
    },
  });
}

export function useDeletePartnerBrandLogo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => partnerBrandsApi.deleteLogo(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partnerBrandKeys.all });
    },
  });
}

/** Upload/replace ONE background-specific variant. Separate from the base-logo
 *  mutations because the variants have no field on the create/update payloads —
 *  these endpoints are their only writer, so a partial update can never
 *  silently clear them. */
export function useUploadPartnerBrandLogoVariant(id: string, variant: PartnerLogoVariant) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => partnerBrandsApi.uploadLogoVariant(id, variant, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partnerBrandKeys.all });
    },
  });
}

export function useDeletePartnerBrandLogoVariant(id: string, variant: PartnerLogoVariant) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => partnerBrandsApi.deleteLogoVariant(id, variant),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partnerBrandKeys.all });
    },
  });
}
